import { deepMerge } from 'toolbox/deepMerge';
import { mix } from 'core/mixwith';
import Component from 'core/Component';
import AnalyticsMixin from 'mixins/Analytics';
import { on, off } from 'toolbox/event';
import { Event } from 'services/EventEmitter';
import { ajax } from 'toolbox/ajax';
import { stringify } from 'toolbox/queryString';
import { getCSRF } from 'toolbox/csrf';

/**
 * This is a description of the DsfServicesButton constructor function.
 * @class
 * @classdesc This is a description of the DsfServicesButton class. (must be edited)
 * @extends Component
 */
export default class DsfServicesButton extends mix(Component).with(AnalyticsMixin) {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            scriptUrl: 'https://dsf-cdn.loreal.io/dropper.js',
            updateCartUrl: null, // add to cart url
            changePrescriptionUrl: null, // change prescription url
            tagName: 'dsf-app',
            tenant: 'lorealsa', // The tenant on the Firecamp CMS (possible values: lorealsa, modiface)
            application: 'haircarediag', // The Service you want to run (possible values: shadefinder, haircarediag, skindrv2, spotscan2, nailstudio)
            customer: null, // The account name of L'Oréal DSF Firecamp back-office. (possible values: keratase, lorealparis)
            country: null, // The localization country code in ISO3 format
            environment: 'dev', // The environment of the Service you want to start (possible values: dev, staging, production)
            range: null, // The range code
            touchpoint: 'web', // Technical value to provided by DSF (possible values: web, pos)
            type: 'std', // Technical value to provided by DSF (possible values: std, canary)
            loadingBg: '#000', // Background color during first stage loading (optional)
            loadingMsg: 'loading', // Message to be display during first stage loading (optional)
            switchEcom: true, // Set to false to disable price & CTA on product tiles (optional)
            switchAutostart: false, // Force the Service to start immediately even if a CTA button is expect (optional)
            vtoType: null, // VTO experience ("sp" - Single Product, "mp" - Multi Product)
            vtoCategory: null, // VTO service category (nails, makeup or hair),
            vtoUPCList: null, // list of product UPCs you want to load in the VTO experience
            vtoSelectedUPC: null, // preselected UPC at the launch of the VTO experience
            vtoSelectedLook: null, // preselected Look at the launch of the VTO experience
            vtoSelectedMakeupCategory: null, // preselected Category at the launch of the VTO experience
            scriptId: 'dsf', // DSF SDK script id
            displayAnalytics: null, // display analytics
            recordUrl: null, // URL for recording DDXL events activity
            dsfEventMapping: {}, // Mapping between DSF services and DDXL events
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.application = document.querySelector(this.options.tagName);
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        on('click', this.element, this.onClick.bind(this));
    }

    /**
    * After init
    * Run any script after the component is fully initialized
    */
    afterInit() {
        if (this.options.autoStart) {
            setTimeout(() => {
                this.element.click();
            }, 1000);
        }

        if (this.options.displayAnalytics) {
            this.sendAnalytics(this.options.displayAnalytics);
        }
    }

    /**
     * Check if DSF initialized
     * @return {Boolean} true, if DSF already initialized
     */
    isDSFInitialized() {
        return !!window.dsfDropper && !!this.selectors.application;
    }

    /**
     * Initialize DSF
     */
    initializeDSF() {
        let element = this.selectors.application;

        if (!element) {
            element = document.createElement(this.options.tagName);
            document.body.appendChild(element);
            this.selectors.application = element;
        }

        this.setApplicationAttributes();
        this.bindApplicationEvents();

        if (!window.dsfDropper) {
            window.dsfDropperLoaded = () => {
                window.dsfDropper.openDsfService(this.options.application);
                window.dsfApp.addEventListener('onPrescriptionChanged', this.onPrescriptionChanged.bind(this));
                window.dsfApp.addEventListener('onFirstUserShadeSelected', this.onFirstUserShadeSelected.bind(this));
                window.dsfApp.addEventListener('onResultPageLoaded', this.onResultPageLoaded.bind(this));
            };
        }

        if (!document.getElementById(this.options.scriptId)) {
            const dropperScript = document.createElement('script');
            dropperScript.setAttribute('id', this.options.scriptId);
            dropperScript.setAttribute('src', this.options.scriptUrl);
            dropperScript.setAttribute('type', 'application/javascript');
            dropperScript.setAttribute('auto-run', 'false');
            dropperScript.onload = this.onScriptLoadSuccess.bind(this);
            document.head.appendChild(dropperScript);
        } else {
            // script already loaded by other service, need to open DSF service
            window.dsfDropper.openDsfService(this.options.application);
        }
    }

    /**
     * Bind DSF events
     */
    bindApplicationEvents() {
        if (!this.selectors.application) {
            return;
        }

        this.selectors.application.addEventListener('addToCart', this.onUpdateCart.bind(this));
    }

    /**
     * Add to cart event handler
     * @param {Object} event event payload
     * @returns {Promise} response promise
     */
    onUpdateCart(event) {
        if (!this.options.updateCartUrl) {
            return Promise.reject();
        }

        const parameters = {};
        let location = null;

        if (event.detail) {
            const quantity = event.detail.quantity === undefined ? 1 : parseInt(event.detail.quantity, 10);

            if (event.detail.location) {
                ({ location } = event.detail);
            }

            if (event.detail.products) {
                event.detail.products.forEach((pid, idx) => {
                    parameters[`pid_${idx}`] = pid;
                    parameters[`qty_${idx}`] = quantity;
                });
            }
        }

        if (location) {
            parameters.location = location;
        }

        return ajax(this.options.updateCartUrl, {
            method: 'POST',
            body: stringify(parameters),
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }).then((response) => {
            let products = [];

            // get added products from response
            products = (response && response.products) || [];
            // trigger DSF service addedToCart action
            this.selectors.application.addedToCart(products);
        }).catch(() => {
            this.selectors.application.addedToCart([]);
        });
    }


    /**
     * Prescription changed event handler
     * @param {Object} event event payload
     * @returns {Promise} response promise
     */
    onPrescriptionChanged(event) {
        if (!this.options.changePrescriptionUrl) {
            return Promise.reject();
        }

        const products = [];
        event.detail.forEach((item) => {
            item.steps.forEach((step) => {
                step.products.forEach((product) => {
                    if (products.indexOf(product.data.EAN) === -1) {
                        products.push(product.data.EAN);
                    }
                });
            });
        });

        if (products.length) {
            return ajax(this.options.changePrescriptionUrl, {
                method: 'POST',
                body: stringify({
                    products: products.join(','),
                    application: this.options.application,
                }),
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
        }

        return Promise.resolve();
    }

    /**
     * Sends a request to record the event of the first user shade selection.
     * @param {Object} event event payload
     * @returns {void}
     */
    onFirstUserShadeSelected(event) {
        const eventID = this.options.dsfEventMapping[event.detail.service_name];
        this.sendEventRequest(this.options.recordUrl, eventID);
    }

    /**
     * Sends a request to record the event when the results page is loaded.
     * This applies to services such as Hair Diagnostic, Shade Finder, Skin Dr V3, Face Architect and Fragrance Finder.
     * @param {Object} event event payload
     * @returns {void}
     */
    onResultPageLoaded(event) {
        const eventID = this.options.dsfEventMapping[event.detail.service_name];
        this.sendEventRequest(this.options.recordUrl, eventID);
    }

    /**
     * Helper function to handle AJAX requests with event data.
     * @param {string} recordUrl - The URL to send the request.
     * @param {string} eventID - The event ID to include in the request body.
     * @returns {void}
     */
    sendEventRequest(recordUrl, eventID) {
        if (!recordUrl || !eventID) {
            throw new Error('Request could not be processed.');
        }
        const data = { recordEventID: eventID };
        const requestBody = {
            data: JSON.stringify(data),
        };
        const csrf = getCSRF();
        if (csrf) {
            requestBody[csrf.name] = csrf.token;
        }
        ajax(this.options.recordUrl, {
            method: 'POST',
            body: requestBody,
        });
    }

    /* eslint-disable complexity */
    /**
     * Method to handle click event
     * @param {Object} event event object
     */
    onClick(event) {
        // try to get DSF button, during click
        // in can be empty during component initialization, but can be inserted into DOM after other DSF button was clicked
        if (!this.selectors.application) {
            this.selectors.application = document.querySelector(this.options.tagName);
        }

        if (this.isDSFInitialized()) {
            this.setApplicationAttributes();
            window.dsfDropper.openDsfService(this.options.application, { forceOpening: true });
        } else {
            this.initializeDSF();
        }

        const target = event.currentTarget;
        setTimeout(() => {
            const analytics = this.getAnalyticsAttributeData(target);
            this.sendAnalytics(analytics);
        }, 0);
    }

    /**
     * Set DSF attribute to DSF web component
     * @param {Element} element DSF DOM element
     */
    setApplicationAttributes() {
        const element = this.selectors.application;

        // remove all attributes before setting new one
        while (element.attributes.length > 0) {
            element.removeAttribute(element.attributes[0].name);
        }

        element.setAttribute('tenant', this.options.tenant);
        element.setAttribute('application', this.options.application);
        element.setAttribute('customer', this.options.customer);
        element.setAttribute('country', this.options.country);
        element.setAttribute('environment', this.options.environment);
        element.setAttribute('touchpoint', this.options.touchpoint);
        element.setAttribute('type', this.options.type);
        element.setAttribute('loadingbg', this.options.loadingBg);
        element.setAttribute('loadingmsg', this.options.loadingMsg);
        element.setAttribute('switch_ecom', this.options.switchEcom);
        element.setAttribute('switch_autostart', this.options.switchAutostart);
        const csrf = getCSRF();
        if (csrf) {
            element.setAttribute('csrf_token', csrf.token);
        }

        if (this.options.range) {
            element.setAttribute('range', this.options.range);
        }

        // VTO attributes
        if (this.options.vtoType) {
            element.setAttribute('vto_type', this.options.vtoType);
            element.setAttribute('vto_category', this.options.vtoCategory);

            if (this.options.vtoUPCList) {
                element.setAttribute('vto_upc_list', this.options.vtoUPCList);
            }

            if (this.options.vtoSelectedUPC) {
                element.setAttribute('vto_selected_upc', this.options.vtoSelectedUPC);
            }

            if (this.options.vtoSelectedLook) {
                element.setAttribute('vto_selected_look', this.options.vtoSelectedLook);
            }

            if (this.options.vtoSelectedMakeupCategory) {
                element.setAttribute('vto_selected_makeup_category', this.options.vtoSelectedMakeupCategory);
            }
        }
    }

    /**
     * Perform actions after scripts and styles load
     */
    onScriptLoadSuccess() {
        if (!window.dsfUpdateMinicart) {
            window.dsfUpdateMinicart = () => {
                Event.emit('cart.product.added', {
                    showMiniCart: false,
                });
            };
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        off('click', this.element, this.onClick.bind(this));
    }
}
